import { useRef, useEffect, useState } from 'react';
import { testimonials } from '../../../mocks/home';

const Testimonials = () => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-24 bg-cream" id="reviews">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="h-0.5 w-8 bg-teal"></div>
            <span className="text-teal font-body text-xs tracking-widest uppercase font-medium">Testimonials</span>
            <div className="h-0.5 w-8 bg-teal"></div>
          </div>
          <h2 className="font-heading font-800 text-navy text-4xl lg:text-5xl mb-4">
            What Our Customers Say
          </h2>
          <p className="text-charcoal/60 font-body text-lg max-w-xl mx-auto">
            231 five-star Google reviews and counting. Here&apos;s what real Minneapolis customers say about TrueCare.
          </p>
        </div>

        {/* Reviews Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((review, i) => (
            <div
              key={i}
              className={`bg-white rounded-2xl p-7 border border-transparent hover:border-teal/20 transition-all duration-700 flex flex-col ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: visible ? `${i * 80}ms` : '0ms' }}
            >
              {/* Stars + Tag */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <i key={s} className="ri-star-fill text-teal text-sm"></i>
                  ))}
                </div>
                <span className="bg-teal/10 text-teal font-body text-xs px-2.5 py-1 rounded-full font-medium">{review.tag}</span>
              </div>

              {/* Quote icon */}
              <div className="w-8 h-8 flex items-center justify-center mb-3">
                <i className="ri-double-quotes-l text-teal/30 text-3xl"></i>
              </div>

              {/* Text */}
              <p className="text-charcoal/70 font-body text-sm leading-relaxed flex-1 mb-5">{review.text}</p>

              {/* Reviewer */}
              <div className="flex items-center justify-between pt-4 border-t border-navy/8">
                <div>
                  <div className="font-heading font-700 text-navy text-sm">{review.name}</div>
                  <div className="text-charcoal/50 font-body text-xs mt-0.5">{review.location}</div>
                </div>
                <div className="flex items-center gap-1.5 text-text-muted">
                  <i className="ri-google-fill text-sm"></i>
                  <span className="font-body text-xs">Google Review</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className={`text-center mt-12 transition-all duration-700 delay-500 ${visible ? 'opacity-100' : 'opacity-0'}`}>
          <a
            href="https://www.google.com/search?q=TrueCare+Moving+Services+Minneapolis"
            target="_blank"
            rel="nofollow noreferrer"
            className="inline-flex items-center gap-2 border-2 border-navy/20 hover:border-teal text-navy hover:text-teal font-heading font-600 px-6 py-3 rounded-xl transition-all duration-200 cursor-pointer text-sm"
          >
            <i className="ri-external-link-line"></i>
            View All 231 Google Reviews
          </a>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
